import React from 'react';
import {
  Sparkles,
  Users,
  Lock,
  Image as ImageIcon,
  CalendarHeart,
  MessageCircle,
  Infinity as InfinityIcon,
} from 'lucide-react';

interface Pillar {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  tint: string;
}

const pillars: Pillar[] = [
  {
    icon: Users,
    title: 'Bring your family back together',
    body: 'KinsCircle gives relatives one private place to reconnect, talk, remember, and grow closer again.',
    tint: 'from-fuchsia-500 to-pink-500',
  },
  {
    icon: MessageCircle,
    title: 'Talk without outside distractions',
    body: 'Private messages, family updates, voice calls, and video calls — no strangers, no influencers, no public timeline.',
    tint: 'from-fuchsia-500 to-purple-600',
  },
  {
    icon: ImageIcon,
    title: 'Preserve memories that matter',
    body: 'Photos, videos, stories, birthdays, milestones, and family history stay connected to the people they belong to.',
    tint: 'from-pink-500 to-rose-500',
  },
  {
    icon: InfinityIcon,
    title: 'Built for every kind of family',
    body: 'Parents, children, grandparents, cousins, in-laws, step-family, blended households, and every generation belong here.',
    tint: 'from-purple-500 to-fuchsia-500',
  },
  {
    icon: Lock,
    title: 'Private by design',
    body: 'Your family circle is not social media. No public profiles, no ads, no strangers — just your family.',
    tint: 'from-rose-500 to-pink-500',
  },
  {
    icon: CalendarHeart,
    title: 'A reason to show up',
    body: 'Birthdays, reunions, memories, and family moments help give everyone a reason to come back together.',
    tint: 'from-rose-500 to-fuchsia-500',
  },
];

const ComparisonSection: React.FC = () => {
  return (
    <section
      id="compare"
      className="py-24 bg-gradient-to-b from-white via-pink-50/40 to-white relative overflow-hidden"
    >
      <div aria-hidden className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full bg-pink-200/40 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-fuchsia-200/40 blur-3xl" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur border border-pink-200 shadow-sm mb-5">
            <Sparkles className="w-4 h-4 text-fuchsia-600" />
            <span className="text-xs font-semibold tracking-wide uppercase text-fuchsia-700">
              Why KinsCircle
            </span>
          </div>

          <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 mb-5 leading-tight">
            Bring your family back together{' '}
            <span className="bg-gradient-to-r from-fuchsia-600 via-pink-500 to-rose-500 bg-clip-text text-transparent">
              in one private place
            </span>
          </h2>

          <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
            There was a time when families were closer. Birthdays, reunions, stories,
            laughter, cousins, grandparents, and memories kept everyone connected.
            Over time, life got busy, people moved away, phone numbers changed, and
            social media connected us to the world while many families drifted apart.
            KinsCircle was created to give families a reason to reunite, rebuild
            kinship, and grow together again.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="group relative bg-white rounded-2xl p-7 border border-slate-200/70 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${p.tint} flex items-center justify-center shadow-md mb-5`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-2">
                  {p.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-[15px]">
                  {p.body}
                </p>
                <div className={`absolute left-7 right-7 bottom-5 h-0.5 rounded-full bg-gradient-to-r ${p.tint} opacity-0 group-hover:opacity-100 transition-opacity`} />
              </div>
            );
          })}
        </div>

        <div className="mt-16 relative">
          <div className="rounded-3xl bg-gradient-to-br from-fuchsia-600 via-pink-600 to-rose-600 p-[1.5px] shadow-2xl">
            <div className="rounded-3xl bg-slate-950 px-8 md:px-14 py-12 md:py-14 text-center relative overflow-hidden">
              <div aria-hidden className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,rgba(236,72,153,0.35),transparent_55%),radial-gradient(circle_at_80%_60%,rgba(168,85,247,0.35),transparent_55%)]" />
              <div className="relative">
                <img
              src="/brand/kinscircle-tree-icon.png"
              alt="KinsCircle"
              className="w-16 h-16 rounded-2xl shadow-lg mx-auto mb-6"
            />

                <h3 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  Social media shows you everyone else.
                  <br className="hidden sm:block" />
                  KinsCircle shows you family.
                </h3>

                <p className="text-pink-100 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                  Facebook shows strangers. Instagram shows influencers. TikTok shows trends.
                  KinsCircle shows grandparents, parents, children, grandchildren, cousins,
                  aunts, uncles, memories, and the people who matter most.
                </p>

                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <a href="#tree" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-fuchsia-700 font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition">
                    <Users className="w-4 h-4" />
                    Start your family circle
                  </a>
                  <a href="#gallery" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 text-white font-semibold border border-white/25 hover:bg-white/20 transition">
                    <ImageIcon className="w-4 h-4" />
                    Preserve your memories
                  </a>
                </div>

                <p className="mt-8 text-white font-serif text-2xl">
                  Where Family Comes Full Circle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
